/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import classname from 'classnames';

import { IDynamicImagesData } from '../../themes/mattressfirm/definition-extensions/content-block.ext.props.autogenerated';
import { navLinkKeywordRemoval } from '../../Utilities/plp-url-utils';
import { IMfrmHeroSlideData } from './mfrm-hero-slide.data';
import { IMfrmHeroSlideProps } from './mfrm-hero-slide.props.autogenerated';
export interface IMfrmHeroSlideViewProps extends IMfrmHeroSlideProps<IMfrmHeroSlideData> {
    eyebrow?: string;
    subCopy?: React.ReactNode;
    ctaCopy?: string;
    image?: React.ReactNode;
    imageAriaLabel?: string;
    seoText?: React.ReactNode;
    link?: string;
    className?: string;
    useDynamicImages?: boolean;
    dynamicImages?: IDynamicImagesData;
    enhancedEcommTracking?: string;
    heading?: React.ReactNode;
    heroLayout?: string;
    heroColor?: string;
    heroTextColor?: string;
}

/**
 *
 * MfrmHeroSlide component
 * @extends {React.PureComponent<IMfrmHeroSlideProps<IMfrmHeroSlideData>>}
 */
class MfrmHeroSlide extends React.PureComponent<IMfrmHeroSlideProps<IMfrmHeroSlideData>> {
    public render(): JSX.Element | null {
        const {
            eyebrow,
            subCopy,
            ctaCopy,
            image,
            imageAriaLabel,
            seoText,
            className,
            useDynamicImages,
            dynamicImages,
            enhancedEcommTracking,
            heading,
            heroLayout,
            heroColor,
            heroTextColor
        } = this.props.config;

        const imageProps = {
            gridSettings: this.props.context.request.gridSettings || {},
            imageSettings: image && image.imageSettings
        };

        const heroImage = image && (
            <Msdyn365.Image
                {...image}
                {...imageProps}
                editProps={{ key: this.props.config.image || {}, requestContext: this.props.context.request }}
            />
        );
        const subcopyText = subCopy && (
            <Msdyn365.RichTextComponent
                text={subCopy}
                className='ms-content-block__text'
                editProps={{ onEdit: this.handleSubCopyChange, requestContext: this.props.context.request }}
            />
        );

        const headerRichText = heading && (
            <Msdyn365.RichTextComponent
                text={heading}
                className={classname('ms-content-block__text', this._getTextColor(heroTextColor))}
                editProps={{ onEdit: this.handleSubCopyChange, requestContext: this.props.context.request }}
            />
        );
        const seoRichText = seoText && (
            <Msdyn365.RichTextComponent
                text={seoText}
                className='ms-content-block__text'
                editProps={{ onEdit: this.handleSubCopyChange, requestContext: this.props.context.request }}
            />
        );
        const heroSlideViewProps = {
            ...this.props,
            eyebrow,
            subCopy: subcopyText,
            ctaCopy,
            image: heroImage,
            moduleClass: className,
            link: this._getLink(),
            imageAriaLabel,
            seoText: seoRichText,
            className,
            useDynamicImages,
            dynamicImages,
            enhancedEcommTracking,
            heading: headerRichText,
            heroLayout,
            heroTextColor: this._getTextColor(heroTextColor),
            heroColor: this._getBkgColor(heroColor)
        };

        return this.props.renderView(heroSlideViewProps);
    }


    public handleSubCopyChange = (event: Msdyn365.ContentEditableEvent) => this.props.config.heading = event.target.value;

    private _getLink(): string | null {
        const { link } = this.props.config;
        if (link) {
            return navLinkKeywordRemoval(link.destinationUrl, this.props.context.request);
        }
        return null;
    }

    private _getTextColor(heroTextColor: any) {
        let textColor;
        switch (heroTextColor) {
            case 'white':
            textColor = 'font-white';
            break;
            case 'black':
            textColor = 'font-black';
            break;
        }

        return textColor;
    }

    private _getBkgColor(heroColor: any) {
        let heroBkgColor;
            switch (heroColor) {
                case 'red':
                heroBkgColor = 'bkg-red';
                break;
                case 'blue':
                heroBkgColor = 'bkg-blue';
                break;
                case 'white':
                heroBkgColor = 'bkg-white';
                break;
            }
         return heroBkgColor;
    }

}

export default MfrmHeroSlide;
